import { Component, ElementRef, Input, OnChanges, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { AttachmentService, Attachment, AttachmentHierarchyNode } from '../../../services/attachment-service';
import { LoginService } from '../../../services/login-service';
import { ModalType } from '../../utility/dialog-template/dialog-template.component';
import { DialogService } from '../../utility/dialog/dialog-service';

interface Breadcrumb {
  id: string | null;
  name: string;
}

@Component({
  selector: 'app-attachment',
  templateUrl: './attachment.component.html',
  styleUrls: ['./attachment.component.css'],
  standalone: false
})
export class AttachmentComponent implements OnInit, OnChanges {
  @Input() clientId!: string;
  @Input() recordId!: string;
  @Input() dataSetId?: string | null;

  hierarchyTree: AttachmentHierarchyNode[] = [];
  attachments: Attachment[] = [];
  breadcrumb: Breadcrumb[] = [];
  selectedFile: File | null = null;
  childFolders: AttachmentHierarchyNode[] = [];
  attachmentsByFolder: Record<string, Attachment[]> = {};
  currentChildFolders: AttachmentHierarchyNode[] = [];
  description: string = '';
  effectiveDate: Date | null = null;
  allAttachments: Attachment[] = [];

  @ViewChild('fileInput') fileInput!: ElementRef;

  constructor(private attachmentService: AttachmentService, private loginService: LoginService, private dialog: DialogService) { }

  ngOnInit(): void {
    if (this.clientId) this.reloadHierarchy();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['clientId'] && !changes['clientId'].firstChange && this.clientId) {
      this.reloadHierarchy();
    }
  }

  private reloadHierarchy(): void {
    this.breadcrumb = [{ id: null, name: 'Home' }];
    this.loadHierarchyTree();
    this.loadAttachments();
  }

  private loadHierarchyTree(): void {
    this.attachmentService.getHierarchyTree(
      this.clientId,
      this.recordId,
      null,
      this.dataSetId ?? null,
      (data: AttachmentHierarchyNode[]) => {

        console.log('=== HIERARCHY TREE RESPONSE ===');
        console.log(data);

        console.log('=== HIERARCHY TREE JSON ===');
        console.log(JSON.stringify(data, null, 2));

        this.hierarchyTree = data ?? [];

        console.log('=== ASSIGNED HIERARCHY TREE ===');
        console.log(this.hierarchyTree);

        this.updateChildFolders();
        this.logCurrentView();
      }
    );
  }

  private loadAttachments(): void {
    const currentHierarchyId = this.breadcrumb[this.breadcrumb.length - 1]?.id ?? null;

    this.attachmentService.getAttachmentsWithHierarchy(
      this.clientId,
      this.recordId,
      currentHierarchyId,
      undefined,
      this.dataSetId ?? null,
      undefined,
      undefined,
      (data: Attachment[]) => {

        console.log('RAW attachments from backend:', data);

        this.attachments = data ?? [];
        this.allAttachments = data ?? [];
        this.logCurrentView();
        console.log('Attachments shown in UI:', this.attachments);
      }
    );
  }
  //debugging purposes
  logCurrentView(): void {
    const currentId = this.breadcrumb[this.breadcrumb.length - 1]?.id ?? null;

    const path = this.breadcrumb.map(b => b.name).join('/');

    const folders = this.currentChildFolders ?? [];

    const files = this.getAttachmentsForCurrentFolder();

    console.log('CURRENT PATH:', path);
    console.log('CURRENT HIERARCHY ID:', currentId);

    console.log('FOLDERS IN THIS PATH:');
    folders.forEach(f => {
      console.log(`- ${f.folderName} (${f.id})`);
    });

    console.log('FILES IN THIS PATH:');
    files.forEach(f => {
      console.log(`- ${f.fileName} (HierarchyId: ${f.hierarchyId})`);
    });

    console.log('-----------------------------');
  }

  getAttachmentsForCurrentFolder(): Attachment[] {
    const currentId = this.breadcrumb[this.breadcrumb.length - 1]?.id ?? null;

    return this.allAttachments.filter(a => {
      const folderId = a.hierarchyId ?? null;

      // ROOT VIEW
      if (!currentId) {
        return folderId === null || folderId === undefined;
      }

      // CHILD FOLDER VIEW
      return folderId === currentId;
    });
  }

  private updateChildFolders(): void {
    const currentId = this.breadcrumb[this.breadcrumb.length - 1]?.id ?? null;

    const findNode = (nodes: AttachmentHierarchyNode[]): AttachmentHierarchyNode | null => {
      for (const node of nodes) {
        if (node.id === currentId) return node;
        const child = findNode(node.children);
        if (child) return child;
      }
      return null;
    };

    const currentNode = currentId
      ? findNode(this.hierarchyTree)
      : { children: this.hierarchyTree } as AttachmentHierarchyNode;

    this.currentChildFolders = currentNode?.children ?? [];
  }

  navigateToBreadcrumb(crumb: Breadcrumb, index: number): void {
    this.breadcrumb = this.breadcrumb.slice(0, index + 1);
    this.loadAttachments();      
    this.updateChildFolders();   
    this.logCurrentView();
  }
  openFolder(folder: AttachmentHierarchyNode): void {
    this.breadcrumb.push({ id: folder.id, name: folder.folderName });
    this.loadAttachments();
    this.updateChildFolders();
    this.logCurrentView();
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;

    if (!input.files?.length) {
      this.selectedFile = null;
      return;
    }

    this.selectedFile = input.files[0];
  }

  upload(): void {

    console.log('effective dat', this.effectiveDate)
    if (!this.selectedFile) {
      console.warn('No file selected for upload.');
      return;
    }

    console.log('Effective Date before upload:', this.effectiveDate);

    const currentHierarchy =
      this.breadcrumb?.length
        ? this.breadcrumb[this.breadcrumb.length - 1]?.id
        : null;

    console.log('Hierarchy ID before upload:', currentHierarchy);

    const user = this.loginService.userOptional;
    if (!user) {
      console.error('User not loaded. Cannot determine username.');
      return;
    }

    const client = user.availableClients.find(c => c.id === this.clientId);
    if (!client) {
      console.error(`No client found for clientId: ${this.clientId}`);
      return;
    }

    const userName = client.displayName ?? client.id;
    console.log('Username for upload:', userName);

    const pathParts: string[] =
      this.breadcrumb
        ?.map(b => b.name === 'Home' ? 'home' : b.name)
        .filter(Boolean) ?? [];

    const path = pathParts.join('/');
    console.log('Uploading to path:', path);
    // Default to today if not selected
    const effectiveDateToUse = this.effectiveDate ?? new Date();

    console.log('Effective Date before upload:', effectiveDateToUse);
    this.attachmentService.uploadAttachment(
      this.clientId,
      path,
      this.selectedFile,
      userName,
      this.description,
      //this.effectiveDate,
      effectiveDateToUse, 
      currentHierarchy,  
      this.recordId ?? null,  
      this.dataSetId ?? null  
    ).subscribe({
      next: (res: any) => {
        console.log('Upload success:', res);

        this.selectedFile = null;

        if (this.fileInput) {
          this.fileInput.nativeElement.value = '';  
        }

        this.loadAttachments();
      },
      error: (err: any) => {
        console.error('Upload failed:', err);
      }
    });
  }
  
  download(att: Attachment): void {
    this.attachmentService.downloadAttachment(
      att.id,
      att.fileName, 
      (success: boolean) => {
        if (!success) {
          alert('Failed to download file.');
        }
      }
    );
  }
  hasAttachments(folderId: string): boolean {
    return this.allAttachments.some(a => a.hierarchyId === folderId);
  }

  delete(att: Attachment): void {
    const user = this.loginService.userOptional;
    if (!user) {
      console.error('User not loaded.');
      return;
    }

    const client = user.availableClients.find(c => c.id === this.clientId);
    if (!client) {
      console.error(`No client found for clientId: ${this.clientId}`);
      return;
    }

    const userName = client.displayName ?? client.id;

    this.dialog.openModal(
      'Confirm Deletion',
      `Are you sure you want to delete '${att.fileName}'?`,
      ModalType.QUESTION,
      () => {
        console.log('Deleting as user:', userName);

        this.attachmentService.deleteAttachment(
          att.id,
          userName,
          () => {
            //Refresh UI after delete
            this.loadHierarchyTree();
            this.loadAttachments();

            // Optional success modal (recommended for UX consistency)
            this.dialog.openModal(
              'Deleted',
              `'${att.fileName}' has been deleted.`,
              ModalType.INFO
            );
          }
        );
      }
    );
  }

  getChildFolders(): AttachmentHierarchyNode[] {
    const currentId = this.breadcrumb[this.breadcrumb.length - 1]?.id ?? null;

    const findNode = (nodes: AttachmentHierarchyNode[]): AttachmentHierarchyNode | null => {
      for (const node of nodes) {
        if (node.id === currentId) return node;
        const child = findNode(node.children);
        if (child) return child;
      }
      return null;
    };

    const currentNode = currentId ? findNode(this.hierarchyTree) : { children: this.hierarchyTree } as AttachmentHierarchyNode;
    return currentNode?.children ?? [];
  }

  formatBytes(bytes: number): string {
    if (!bytes) return '0 B';
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`;
  }

  openFileDialog(): void {
    this.fileInput.nativeElement.click();
  }

  cancelSelection(): void {
    this.selectedFile = null;

    if (this.fileInput) {
      this.fileInput.nativeElement.value = '';
    }
    this.updateChildFolders();
  }

  private folderHasAnyAttachmentRecursive(folder: AttachmentHierarchyNode): boolean {
    const hasDirect = this.allAttachments.some(
      a => a.hierarchyId === folder.id
    );

    if (hasDirect) return true;

    return (folder.children ?? []).some(child =>
      this.folderHasAnyAttachmentRecursive(child)
    );
  }

  isFolderInactive(folder: AttachmentHierarchyNode): boolean {
    return !this.folderHasAnyAttachmentRecursive(folder);
  }
  
}
